import React, { useEffect, useState } from 'react';
import { Table, Tag, Typography, Select, Button, Alert, Card, Badge, Modal, Form, DatePicker, Input, message, Divider, Drawer, Descriptions, List, Spin } from 'antd';
import { PlusOutlined, TeamOutlined, DeleteOutlined } from '@ant-design/icons';
import { getProjects, getMOMs, getUsers, createMOM, createMOMAction, getMOMActions } from '../services/api';

const { Title, Text } = Typography;
const { TextArea } = Input;

interface Project {
  id: string;
  code: string;
  name: string;
}

interface UserOption {
  id: string;
  full_name: string;
}

const severityColor = (severity: string) => {
  switch (severity) {
    case 'CRITICAL': return 'red';
    case 'MAJOR': return 'orange';
    case 'MINOR': return 'green';
    default: return 'default';
  }
};

const statusColor = (status: string) => {
  switch (status) {
    case 'CREATED': return 'blue';
    case 'ASSIGNED': return 'cyan';
    case 'UNDER_REVIEW': return 'purple';
    case 'APPROVED': return 'green';
    case 'CLOSED': return 'default';
    case 'REJECTED': return 'red';
    default: return 'default';
  }
};

const MOMPage: React.FC = () => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedProject, setSelectedProject] = useState<string>('');
  const [moms, setMoms] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [modalOpen, setModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm();
  const [users, setUsers] = useState<UserOption[]>([]);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedMOM, setSelectedMOM] = useState<any>(null);
  const [actions, setActions] = useState<any[]>([]);
  const [actionsLoading, setActionsLoading] = useState(false);

  useEffect(() => {
    loadProjects();
    loadUsers();
  }, []);

  const loadProjects = async () => {
    try {
      const response = await getProjects();
      setProjects(response.data);
      if (response.data.length > 0) {
        setSelectedProject(response.data[0].id);
        loadMOMs(response.data[0].id);
      }
    } catch (err) {
      setError('Failed to load projects.');
    }
  };

  const loadUsers = async () => {
    try {
      const usersRes = await getUsers();
      setUsers(usersRes.data);
    } catch (err) {
      // Non-blocking — names simply won't resolve if this fails
    }
  };

  const loadMOMs = async (projectId: string) => {
    setLoading(true);
    try {
      const response = await getMOMs(projectId);
      setMoms(response.data);
    } catch (err) {
      setError('Failed to load MOMs.');
    } finally {
      setLoading(false);
    }
  };

  const handleProjectChange = (value: string) => {
    setSelectedProject(value);
    loadMOMs(value);
  };

  const userName = (id: string) => users.find(u => u.id === id)?.full_name || id;

  const openDrawer = async (record: any) => {
    setSelectedMOM(record);
    setDrawerOpen(true);
    setActions([]);
    setActionsLoading(true);
    try {
      const actionsRes = await getMOMActions(record.id);
      setActions(actionsRes.data);
    } catch (err) {
      // Non-blocking — Drawer still shows meeting details even if actions fail to load
    } finally {
      setActionsLoading(false);
    }
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setSelectedMOM(null);
    setActions([]);
  };

  const columns = [
    {
      title: 'Meeting Type',
      dataIndex: 'meeting_type',
      key: 'meeting_type',
      render: (text: string) => (
        <Text strong style={{ color: '#1B2A4A' }}>{text}</Text>
      ),
    },
    {
      title: 'Meeting Date',
      dataIndex: 'meeting_date',
      key: 'meeting_date',
      render: (date: string) => (
        new Date(date).toLocaleDateString('en-IN', {
          day: '2-digit', month: 'short', year: 'numeric'
        })
      ),
    },
    {
      title: 'Location',
      dataIndex: 'meeting_location',
      key: 'meeting_location',
    },
    {
      title: 'Attendees',
      dataIndex: 'attendees',
      key: 'attendees',
      render: (attendees: string[]) => (
        <Badge
          count={attendees?.length || 0}
          style={{ backgroundColor: '#2E75B6' }}
        >
          <TeamOutlined style={{ fontSize: 18, color: '#2E75B6' }} />
        </Badge>
      ),
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date: string) => new Date(date).toLocaleDateString('en-IN'),
    },
  ];

  const openModal = async () => {
    form.resetFields();
    setModalOpen(true);
    if (users.length === 0) {
      loadUsers();
    }
  };

  const closeModal = () => {
    setModalOpen(false);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSubmitting(true);

      const momPayload = {
        meeting_type: values.meeting_type,
        meeting_date: values.meeting_date.format('YYYY-MM-DD'),
        meeting_location: values.meeting_location,
        chair: values.chair,
        attendees: values.attendees || [],
        absentees: values.absentees || [],
        agenda_summary: values.agenda_summary,
        acknowledgment_deadline: values.acknowledgment_deadline
          ? values.acknowledgment_deadline.format('YYYY-MM-DD')
          : null,
      };

      const momResponse = await createMOM(selectedProject, momPayload);
      const momId = momResponse.data.id;

      const actionItems = values.actions || [];
      for (const action of actionItems) {
        if (!action || !action.title) continue;
        await createMOMAction(momId, {
          title: action.title,
          assigned_to_id: action.assigned_to_id,
          severity: action.severity || 'MAJOR',
          due_date: action.due_date.format('YYYY-MM-DD'),
          remarks: action.remarks || '',
        });
      }

      message.success('MOM created successfully.');
      setModalOpen(false);
      loadMOMs(selectedProject);
    } catch (err: any) {
      if (err?.errorFields) {
        return;
      }
      const apiMessage = err?.response?.data?.detail
        || err?.response?.data?.attendees?.[0]
        || err?.response?.data?.meeting_date?.[0]
        || 'Failed to create MOM.';
      message.error(apiMessage);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <Title level={4} style={{ margin: 0, color: '#1B2A4A' }}>
          MOM Register
        </Title>
        <div style={{ display: 'flex', gap: 12 }}>
          <Select
            value={selectedProject}
            onChange={handleProjectChange}
            style={{ width: 250 }}
            options={projects.map(p => ({ value: p.id, label: `${p.code} — ${p.name}` }))}
          />
          <Button type="primary" icon={<PlusOutlined />} style={{ background: '#1B2A4A' }} onClick={openModal} disabled={!selectedProject}>
            Create MOM
          </Button>
        </div>
      </div>

      {error && <Alert message={error} type="error" showIcon style={{ marginBottom: 24 }} />}

      <Card style={{ borderRadius: 12 }}>
        <Table
          columns={columns}
          dataSource={moms}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 10 }}
          locale={{ emptyText: 'No MOMs found for this project.' }}
          onRow={(record) => ({
            onClick: () => openDrawer(record),
            style: { cursor: 'pointer' },
          })}
        />
      </Card>

      <Drawer
        title={selectedMOM ? `${selectedMOM.meeting_type} — ${new Date(selectedMOM.meeting_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}` : 'MOM Details'}
        open={drawerOpen}
        onClose={closeDrawer}
        width={560}
      >
        {selectedMOM && (
          <>
            <Descriptions column={1} bordered size="small">
              <Descriptions.Item label="Meeting Location">{selectedMOM.meeting_location || '—'}</Descriptions.Item>
              <Descriptions.Item label="Chairperson">{selectedMOM.chair ? userName(selectedMOM.chair) : '—'}</Descriptions.Item>
              <Descriptions.Item label="Attendees">
                {selectedMOM.attendees?.length ? selectedMOM.attendees.map((id: string) => userName(id)).join(', ') : '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Absentees">
                {selectedMOM.absentees?.length ? selectedMOM.absentees.map((id: string) => userName(id)).join(', ') : '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Agenda Summary">{selectedMOM.agenda_summary || '—'}</Descriptions.Item>
              <Descriptions.Item label="Acknowledgment Deadline">
                {selectedMOM.acknowledgment_deadline ? new Date(selectedMOM.acknowledgment_deadline).toLocaleDateString('en-IN') : '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Created">
                {selectedMOM.created_at ? new Date(selectedMOM.created_at).toLocaleDateString('en-IN') : '—'}
              </Descriptions.Item>
            </Descriptions>

            <Divider>Action Items</Divider>

            {actionsLoading ? (
              <div style={{ textAlign: 'center', padding: 24 }}>
                <Spin />
              </div>
            ) : actions.length === 0 ? (
              <Text type="secondary">No action items recorded for this meeting.</Text>
            ) : (
              <List
                dataSource={actions}
                renderItem={(action: any) => (
                  <List.Item>
                    <Card size="small" style={{ width: '100%', background: '#fafafa' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                        <Text strong>{action.title}</Text>
                        <div style={{ display: 'flex', gap: 4 }}>
                          <Tag color={severityColor(action.severity)}>{action.severity}</Tag>
                          <Tag color={statusColor(action.status)}>{action.status?.replace('_', ' ')}</Tag>
                        </div>
                      </div>
                      <div style={{ fontSize: 13, color: '#595959' }}>
                        <div>Assigned to: {action.assigned_to ? userName(action.assigned_to) : '—'}</div>
                        <div>Due: {action.due_date ? new Date(action.due_date).toLocaleDateString('en-IN') : '—'}</div>
                        {action.remarks && <div>Remarks: {action.remarks}</div>}
                      </div>
                    </Card>
                  </List.Item>
                )}
              />
            )}
          </>
        )}
      </Drawer>

      <Modal
        title="Create Minutes of Meeting"
        open={modalOpen}
        onCancel={closeModal}
        onOk={handleSubmit}
        confirmLoading={submitting}
        okText="Create MOM"
        width={720}
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <div style={{ display: 'flex', gap: 16 }}>
            <Form.Item
              name="meeting_type"
              label="Meeting Type"
              rules={[{ required: true, message: 'Meeting type is required' }]}
              style={{ flex: 1 }}
            >
              <Input placeholder="e.g. Site Review, Technical, Safety" />
            </Form.Item>
            <Form.Item
              name="meeting_date"
              label="Meeting Date"
              rules={[{ required: true, message: 'Meeting date is required' }]}
              style={{ flex: 1 }}
            >
              <DatePicker style={{ width: '100%' }} format="DD-MMM-YYYY" />
            </Form.Item>
          </div>

          <Form.Item
            name="meeting_location"
            label="Meeting Location"
            rules={[{ required: true, message: 'Meeting location is required' }]}
          >
            <Input placeholder="e.g. Site Office, Block A" />
          </Form.Item>

          <div style={{ display: 'flex', gap: 16 }}>
            <Form.Item
              name="chair"
              label="Chairperson"
              rules={[{ required: true, message: 'Chairperson is required' }]}
              style={{ flex: 1 }}
            >
              <Select
                placeholder="Select chairperson"
                options={users.map(u => ({ value: u.id, label: u.full_name }))}
              />
            </Form.Item>
            <Form.Item
              name="acknowledgment_deadline"
              label="Acknowledgment Deadline"
              style={{ flex: 1 }}
            >
              <DatePicker style={{ width: '100%' }} format="DD-MMM-YYYY" />
            </Form.Item>
          </div>

          <Form.Item
            name="attendees"
            label="Attendees"
            rules={[{ required: true, message: 'At least one attendee is required' }]}
          >
            <Select
              mode="multiple"
              placeholder="Select attendees"
              options={users.map(u => ({ value: u.id, label: u.full_name }))}
            />
          </Form.Item>

          <Form.Item name="absentees" label="Absentees">
            <Select
              mode="multiple"
              placeholder="Select absentees (optional)"
              options={users.map(u => ({ value: u.id, label: u.full_name }))}
            />
          </Form.Item>

          <Form.Item name="agenda_summary" label="Agenda Summary">
            <TextArea rows={2} placeholder="Brief agenda — full minutes uploaded as document" />
          </Form.Item>

          <Divider>Action Items</Divider>

          <Form.List name="actions">
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...restField }) => (
                  <Card
                    key={key}
                    size="small"
                    style={{ marginBottom: 12, background: '#fafafa' }}
                    extra={
                      <Button
                        type="text"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => remove(name)}
                      />
                    }
                  >
                    <Form.Item
                      {...restField}
                      name={[name, 'title']}
                      label="Action Title"
                      rules={[{ required: true, message: 'Action title is required' }]}
                    >
                      <Input placeholder="What needs to be done" />
                    </Form.Item>
                    <div style={{ display: 'flex', gap: 16 }}>
                      <Form.Item
                        {...restField}
                        name={[name, 'assigned_to_id']}
                        label="Assigned To"
                        rules={[{ required: true, message: 'Assignee is required' }]}
                        style={{ flex: 1 }}
                      >
                        <Select
                          placeholder="Select assignee"
                          options={users.map(u => ({ value: u.id, label: u.full_name }))}
                        />
                      </Form.Item>
                      <Form.Item
                        {...restField}
                        name={[name, 'severity']}
                        label="Severity"
                        style={{ flex: 1 }}
                      >
                        <Select
                          placeholder="MAJOR"
                          options={[
                            { value: 'CRITICAL', label: 'Critical' },
                            { value: 'MAJOR', label: 'Major' },
                            { value: 'MINOR', label: 'Minor' },
                          ]}
                        />
                      </Form.Item>
                      <Form.Item
                        {...restField}
                        name={[name, 'due_date']}
                        label="Due Date"
                        rules={[{ required: true, message: 'Due date is required' }]}
                        style={{ flex: 1 }}
                      >
                        <DatePicker style={{ width: '100%' }} format="DD-MMM-YYYY" />
                      </Form.Item>
                    </div>
                    <Form.Item {...restField} name={[name, 'remarks']} label="Remarks">
                      <Input placeholder="Optional remarks" />
                    </Form.Item>
                  </Card>
                ))}
                <Form.Item>
                  <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                    Add Action Item
                  </Button>
                </Form.Item>
              </>
            )}
          </Form.List>
        </Form>
      </Modal>
    </div>
  );
};

export default MOMPage;